import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, JOB_TYPES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { JobCard, useSaveToggle } from "@/components/JobCard";
import { Hero, ValueStrip, Categories } from "@/components/landing/HeroSections";
import { HowItWorks, CareerStages, ForEmployersBand, ForCollegesBand } from "@/components/landing/MidSections";
import { PricingSection, TrustSection, FaqSection, FinalCta } from "@/components/landing/BottomSections";

export function useSavedIds() {
  const { user } = useAuth();
  const [ids, setIds] = useState([]);
  useEffect(() => {
    if (user?.role === "seeker") api.get("/seeker/saved-ids").then((r) => setIds(r.data)).catch(() => {});
  }, [user]);
  return [ids, setIds];
}

function FeaturedJobs({ categories, locations }) {
  const [jobs, setJobs] = useState([]);
  const [f, setF] = useState({ category: "", location: "", job_type: "", experience: "" });
  const [savedIds, setSavedIds] = useSavedIds();
  const toggle = useSaveToggle(savedIds, setSavedIds);
  const nav = useNavigate();
  useEffect(() => {
    const p = new URLSearchParams({ limit: "8", sort: "featured" });
    Object.entries(f).forEach(([k, v]) => v && p.set(k, v));
    api.get(`/jobs?${p}`).then((r) => setJobs(r.data.items));
  }, [f]);
  const sel = (k, opts, ph) => (
    <select value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="field" data-testid={`featured-filter-${k}`}>
      <option value="">{ph}</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  const viewAll = () => nav(`/jobs?${new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v)))}`);
  return (
    <section className="container-x pb-16" data-testid="featured-jobs">
      <div className="flex items-end justify-between">
        <div><h2 className="h-section">Featured Jobs</h2><p className="mt-1 text-sm text-slate-500">Handpicked opportunities from employers across India</p></div>
        <button onClick={viewAll} className="flex items-center gap-1 text-sm font-semibold text-[#009a41]" data-testid="featured-view-all">View all jobs <ArrowRight size={14} /></button>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {sel("category", categories.map((c) => [c.slug, c.name]), "All Categories")}
        {sel("location", locations.map((l) => [l, l]), "All Locations")}
        {sel("job_type", Object.entries(JOB_TYPES), "All Job Types")}
        {sel("experience", [["0", "Fresher"], ["2", "Up to 2 years"], ["5", "Up to 5 years"], ["10", "Up to 10 years"]], "All Experience Levels")}
      </div>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {jobs.map((j) => <JobCard key={j.id} job={j} saved={savedIds.includes(j.id)} onSave={toggle} />)}
      </div>
      {jobs.length === 0 && <p className="mt-6 text-sm text-slate-500">No jobs match these filters yet. <Link className="text-[#009a41]" to="/jobs">Browse all jobs</Link></p>}
    </section>
  );
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/jobs/locations").then((r) => setLocations(r.data));
    api.get("/settings/pricing").then((r) => setPricing(r.data));
  }, []);
  const site = window.location.origin;
  const social = (process.env.REACT_APP_SOCIAL_LINKS || "").split(",").map((s) => s.trim()).filter(Boolean);
  const ld = [
    { "@context": "https://schema.org", "@type": "WebSite", name: "Jobfixnow", url: site, slogan: "We Find. You Grow.",
      potentialAction: { "@type": "SearchAction", target: { "@type": "EntryPoint", urlTemplate: `${site}/jobs?q={search_term_string}` }, "query-input": "required name=search_term_string" } },
    { "@context": "https://schema.org", "@type": "Organization", name: "Jobfixnow", url: site, logo: `${site}/images/logo-mark.png`, slogan: "We Find. You Grow.",
      description: "All-industry job platform for India connecting job seekers, employers and colleges.", areaServed: "IN", ...(social.length ? { sameAs: social } : {}) },
  ];
  return (
    <>
      <Seo title="Jobs Across India for Every Career & Industry" path="/" jsonLd={ld}
        description="Every Career. Every Industry. One Platform. Search jobs across India for freshers and experienced professionals — IT, sales, healthcare, logistics, government, remote and more." />
      <Hero />
      <ValueStrip />
      <nav aria-label="Explore Jobfixnow" className="container-x pt-8 text-center text-sm" data-testid="home-keyword-links">
        <Link to="/jobs" className="mx-2 inline-block py-1 font-semibold text-[#009a41] hover:underline">Browse all jobs in India</Link>·
        <Link to="/categories" className="mx-2 inline-block py-1 font-semibold text-[#009a41] hover:underline">Jobs by category &amp; industry</Link>·
        <Link to="/for-employers" className="mx-2 inline-block py-1 font-semibold text-[#009a41] hover:underline">Hire candidates as an employer</Link>·
        <Link to="/for-colleges" className="mx-2 inline-block py-1 font-semibold text-[#009a41] hover:underline">Campus placement software for colleges</Link>·
        <Link to="/career-resources" className="mx-2 inline-block py-1 font-semibold text-[#009a41] hover:underline">Free career guides</Link>·
        <Link to="/salaries" className="mx-2 inline-block py-1 font-semibold text-[#009a41] hover:underline">Salaries by role &amp; city</Link>
      </nav>
      <Categories categories={categories} />
      <FeaturedJobs categories={categories} locations={locations} />
      <HowItWorks />
      <CareerStages />
      <ForEmployersBand />
      <ForCollegesBand />
      <PricingSection pricing={pricing} />
      <TrustSection />
      <FaqSection />
      <FinalCta />
    </>
  );
}
